/**
 * The gallery screen.
 *
 * Owns selection, filters (which live in the URL so a filtered view is
 * shareable), the lightbox, and the drop target for uploads.
 */

import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useT } from "../../lib/i18n.js";
import { useAuth } from "../../lib/auth.js";
import { lazyChunk } from "../../lib/lazyChunk.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { trpc, trpcClient } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { useToast } from "../../components/ui/Toast.js";
import { PhotoGrid } from "./PhotoGrid.js";
import { Lightbox } from "./Lightbox.js";
import { FilterBar } from "./FilterBar.js";
import { SelectionBar } from "./SelectionBar.js";
import { UploadDropzone } from "../upload/UploadDropzone.js";
import { UploadPanel } from "../upload/UploadPanel.js";
import { ShareDialog } from "../share/ShareDialog.js";
import { ExportDock } from "../export/ExportDock.js";
import { useExport } from "../export/useExport.js";
import { useUploadQueue } from "../upload/useUploadQueue.js";
import { groupByAlbum, groupByMonth, useGallery } from "./useGallery.js";
import { AlbumsView } from "../album/AlbumsView.js";
import { AlbumPicker } from "../album/AlbumPicker.js";
import { GroupBar } from "../group/GroupBar.js";
import type { Album } from "../album/types.js";
import { AlbumDialog, type FolderSummary } from "../album/AlbumDialog.js";
import { TrashView } from "../trash/TrashView.js";
import { PhotoEditor } from "../edit/PhotoEditor.js";
import { Slideshow } from "../slideshow/Slideshow.js";
import { DuplicatesPanel } from "../duplicates/DuplicatesPanel.js";
import type { DisplayPerson } from "../faces/people.js";
/*
 * Loaded on demand, and it matters more here than anywhere else in the app.
 * It reaches the ONNX runtime, whose JavaScript alone is a sixth of the whole
 * bundle before a byte of WebAssembly is fetched — so importing it eagerly
 * would make everybody who never presses People pay for it, which is the
 * opposite of what the feature promises.
 */
/* ─────────────────────── Icons for the action row ───────────────────────── */

/*
 * The header actions carry glyphs so a phone can drop their labels.
 *
 * Five words-on-buttons is more than 375px of screen has, and the row was
 * scrolling sideways with the primary action off the end of it. The labels are
 * not removed — they are visually hidden, so a screen reader still announces
 * them and the desktop still shows them.
 */
const iconAttrs = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const AddPhotosIcon = () => (
  <svg {...iconAttrs}>
    <path d="M3 8.5A2 2 0 0 1 5 6.5h2.5l1.2-1.8h6.6l1.2 1.8H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M12 9.5v5M9.5 12h5" />
  </svg>
);

const AlbumPlusIcon = () => (
  <svg {...iconAttrs}>
    <path d="M3 8.5A2 2 0 0 1 5 6.5h3.2l1.6 2H19a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M12 11.5v5M9.5 14h5" />
  </svg>
);

const AlbumEditIcon = () => (
  <svg {...iconAttrs}>
    <path d="M3 8.5A2 2 0 0 1 5 6.5h3.2l1.6 2H19a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M11 17h1.8l4.6-4.6a1.3 1.3 0 0 0-1.8-1.8L11 15.2z" />
  </svg>
);

const PeopleIcon = () => (
  <svg {...iconAttrs}>
    <circle cx="9" cy="9" r="3.2" />
    <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
    <path d="M16 6.4a3.2 3.2 0 0 1 0 5.2M17.5 19a5.5 5.5 0 0 0-2-4.3" />
  </svg>
);

const DuplicatesIcon = () => (
  <svg {...iconAttrs}>
    <rect x="3.5" y="3.5" width="12" height="12" rx="2" />
    <path d="M8.5 20.5h10a2 2 0 0 0 2-2v-10" />
  </svg>
);

const PlayIcon = () => (
  <svg {...iconAttrs}>
    <path d="M8 5.5l11 6.5-11 6.5z" />
  </svg>
);
const PeopleStrip = lazyChunk(() =>
  import("../faces/PeopleStrip.js").then((module) => ({
    default: module.PeopleStrip,
  })),
);
import type { EditableFile } from "../edit/PhotoEditor.js";
import "./GalleryPage.css";
import { EmojiIcon } from "../../components/ui/EmojiIcon.js";

export function GalleryPage() {
  const t = useT();
  const auth = useAuth();
  const { groupId } = useParams({ from: "/authed/g/$groupId" });
  const search = useSearch({ from: "/authed/g/$groupId" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [sharing, setSharing] = useState<string[] | null>(null);
  /**
   * The album dialog's subject: "new" to create, an album to edit, null closed.
   *
   * One dialog does both jobs already; this is only which of them is being
   * asked for. Editing was previously reachable from the albums grid alone,
   * which on a desktop had no link to it — so an album's dates and name could
   * be set once, at creation, and never corrected.
   */
  const [albumDialog, setAlbumDialog] = useState<FolderSummary | "new" | null>(
    null,
  );
  const [editing, setEditing] = useState<EditableFile | null>(null);
  const [playing, setPlaying] = useState(false);
  const [findingDupes, setFindingDupes] = useState(false);
  const [findingPeople, setFindingPeople] = useState(false);
  /*
   * The chosen person lives here rather than in the strip, because it filters
   * the grid — which is the whole reason this stopped being its own screen.
   */
  const [person, setPerson] = useState<DisplayPerson | null>(null);
  const lastClickedIndex = useRef<number | null>(null);
  /*
   * The files this upload actually created, for the face indexer.
   *
   * Held in a ref because `onComplete` is defined before the queue exists, so
   * it cannot close over the task list — and reading the gallery instead would
   * race the refetch that has only just been invalidated.
   */
  const uploadedIds = useRef<string[]>([]);

  const group = useQuery(trpc.groups.get.queryOptions({ groupId }));
  const folders = useQuery(trpc.groups.folders.queryOptions({ groupId }));
  const members = useQuery(trpc.groups.members.queryOptions({ groupId }));

  /*
   * A display name per member, for the "Added by" filter. Falls back through
   * the username to the id, because a member who has not set a display name
   * still has to be pickable.
   */
  /**
   * Who added what, with yourself first and named as such.
   *
   * "Which of these are mine?" is the question behind deleting anything a
   * week later: only your own uploads can be removed unless you are an admin,
   * and nobody remembers which forty of two hundred photographs they added.
   * The filter already existed and buried the answer in an alphabetical list
   * of names, which is the same as not having it.
   */
  const memberOptions = useMemo(() => {
    const all = (members.data ?? []).map((member) => ({
      userId: member.userId,
      name: member.user?.displayName ?? member.user?.username ?? member.userId,
    }));

    const mine = all.find((member) => member.userId === auth.user?.id);
    if (!mine) return all;

    return [
      { ...mine, name: `${mine.name} (you)` },
      ...all.filter((member) => member.userId !== auth.user?.id),
    ];
  }, [members.data, auth.user?.id]);

  /** Everyone in the group by id, so a photograph can name who added it. */
  const memberDirectory = useMemo(() => {
    const byId = new Map<string, { name: string; avatar?: string | null }>();
    for (const member of members.data ?? []) {
      byId.set(member.userId, {
        name:
          member.user?.displayName ?? member.user?.username ?? member.userId,
        avatar: member.user?.avatarImage ?? null,
      });
    }
    return byId;
  }, [members.data]);

  /** Just the names, for the people strip to suggest. */
  const memberNames = useMemo(
    () => memberOptions.map((member) => member.name),
    [memberOptions],
  );

  const filters = useMemo(
    () => ({
      folderId: search.folder,
      kind: search.kind,
      uploaderId: search.by,
      year: search.year,
      favoritesOnly: search.favorites,
    }),
    [search.folder, search.kind, search.by, search.year, search.favorites],
  );

  const gallery = useGallery(groupId, filters, search.sort ?? "taken_desc");

  /*
   * Which years this group has anything in.
   *
   * Asked with every filter *except* the year, or choosing 2024 would leave
   * 2024 as the only option and there would be no way back to the others.
   */
  const timeline = useQuery(
    trpc.media.timeline.queryOptions({
      groupId,
      filter: { ...filters, year: undefined },
    }),
  );

  const years = useMemo(
    () =>
      [...new Set((timeline.data ?? []).map((bucket) => bucket.year))].sort(
        (a, b) => b - a,
      ),
    [timeline.data],
  );
  const albums = useMemo(
    () => (folders.data ?? []).filter((folder) => folder.kind === "trip"),
    [folders.data],
  );

  /*
   * The person filter, applied here rather than on the server.
   *
   * Every other filter on the bar is a database query; this one cannot be,
   * because the server has no idea who is in a photograph — which is the whole
   * point of the design. What exists instead is a list of file ids produced by
   * clustering in this browser, so the narrowing happens here.
   */
  const visibleItems = useMemo(() => {
    if (!person) return gallery.items;
    const wanted = new Set(person.fileIds);
    return gallery.items.filter((item) => wanted.has(item.id));
  }, [gallery.items, person]);

  /*
   * Somebody's photographs are spread through the whole library, so filtering
   * only what happens to be paged in would show a fraction of them — and look
   * exactly like the recognition having missed the rest.
   */
  /*
   * One page per pass, not a loop.
   *
   * This was a `while (gallery.hasNextPage) await gallery.fetchNextPage()`,
   * and `gallery` is captured from the render that started it — so
   * `hasNextPage` was frozen at whatever it was then and could never become
   * false from inside. Once the pages ran out `fetchNextPage()` resolved
   * immediately and the loop spun on microtasks, starving the event loop.
   * React could not re-render, so the value that would end the loop never
   * arrived and neither did the cleanup meant to stop it: it blocked the
   * very update it was waiting for. Choosing a person locked the tab with
   * “Loading more photos…” under a grid of empty tiles.
   *
   * Fetching one page and letting the render deliver the next state reads the
   * fresh value every time, stops on its own, and leaves the browser free to
   * paint between pages.
   */
  useEffect(() => {
    if (!person) return;
    if (!gallery.hasNextPage || gallery.isFetchingNextPage) return;
    void gallery.fetchNextPage();
  }, [
    person,
    gallery.hasNextPage,
    gallery.isFetchingNextPage,
    gallery.fetchNextPage,
  ]);

  /**
   * How photographs are grouped when nobody has said.
   *
   * By album once the group has any, and by month until then. A person who has
   * made albums has said what these photographs are *about*, and that is a more
   * useful shelf than the month they happened in; a group with no albums would
   * get one enormous "Not in an album" heap, which is worse than a date.
   *
   * Only ever the default — the control in the filter bar overrides it, and
   * that choice lives in the URL, so it survives a reload and can be shared.
   */
  const defaultGroupBy: "album" | "month" =
    albums.length > 0 ? "album" : "month";

  const sections = useMemo(
    () =>
      (search.groupBy ?? defaultGroupBy) === "album" && !search.folder
        ? groupByAlbum(visibleItems, albums)
        : groupByMonth(visibleItems),
    [visibleItems, search.groupBy, search.folder, albums, defaultGroupBy],
  );

  const capabilities = useMemo(
    () => new Set(group.data?.capabilities ?? []),
    [group.data?.capabilities],
  );

  /*
   * Which photographs leave the group through a live share link.
   *
   * Only for members who can make links in the first place: to everybody else
   * the badge would be an unexplained mark on somebody else’s photograph.
   */
  const shared = useQuery({
    ...trpc.share.sharedFiles.queryOptions({ groupId }),
    enabled: capabilities.has("share:create"),
  });

  const sharedFileIds = useMemo(
    () => new Set(shared.data?.fileIds ?? []),
    [shared.data],
  );

  /*
   * Memoised, because the slideshow keys its whole signed-URL fetch on this
   * list. Rebuilt inline it was a new array on every render, which re-signed
   * every photograph in the show each time anything at all changed.
   */
  const slideshowItems = useMemo(
    () =>
      gallery.items.map((item) => ({
        id: item.id,
        name: item.name,
        /*
         * Capture time drives the whole show — the ordering, and the gaps that
         * separate one moment from the next. It is always present: the server
         * falls back to the upload time where a camera recorded nothing.
         */
        takenAt: new Date(item.takenAt).getTime(),
        favorited: item.favorited,
        width: item.width,
        height: item.height,
        kind: item.kind,
      })),
    [gallery.items],
  );

  const exporter = useExport(groupId);

  /* Open while the album question is being answered, before the OS picker. */
  const [pickingAlbum, setPickingAlbum] = useState(false);
  /* Set when the album dialog was opened from that question, so making one
     carries on to the files rather than stopping to admire the empty album. */
  const uploadAfterAlbum = useRef(false);

  const uploads = useUploadQueue({
    groupId,
    folderId: search.folder ?? null,
    onComplete: () => {
      void queryClient.invalidateQueries({ queryKey: ["gallery", groupId] });
      void group.refetch();
      void folders.refetch();

      /*
       * Look at who is in what was just uploaded.
       *
       * Declines by itself on a browser that has never done this, so nobody
       * pays for a face model they did not ask for — see `afterUpload`. Not
       * awaited and never surfaced: the photographs are uploaded either way,
       * and this is a convenience running behind them.
       */
      const uploaded = uploadedIds.current;
      uploadedIds.current = [];
      if (uploaded.length > 0) {
        void import("../faces/afterUpload.js").then(({ indexAfterUpload }) =>
          indexAfterUpload(groupId, uploaded),
        );
      }

      /*
       * The moment to mention albums.
       *
       * A group with no albums and photographs freshly in it is the one point
       * where making one is obviously worth doing and obviously easy — the
       * photographs to put in it are right there. Asked before the first
       * upload it is an obstacle between somebody and the thing they came to
       * do; asked a month later there are four hundred photographs to sort.
       *
       * A toast with a button, not a dialog: a suggestion that blocks the
       * screen has stopped being a suggestion. Ignoring it costs nothing and
       * it does not come back, because `folders` will not be empty a second
       * time unless the album was deleted on purpose.
       */
      if (uploaded.length > 0 && (folders.data?.length ?? 0) === 0) {
        toast("These would sit nicely in an album.", {
          action: { label: "Create one", onClick: () => setAlbumDialog("new") },
        });
      }
    },
  });

  /*
   * The gallery poll is already watching these files for the grid, so it is
   * also what tells the upload panel whether each one survived processing.
   * Piggy-backing on it avoids a second poll saying the same thing.
   */
  useEffect(() => {
    uploadedIds.current = uploads.tasks
      .map((task) => task.fileId)
      .filter((fileId): fileId is string => Boolean(fileId));
  }, [uploads.tasks]);

  const { applyServerStatuses } = uploads;
  useEffect(() => {
    applyServerStatuses(
      new Map(gallery.items.map((item) => [item.id, item.status] as const)),
    );
  }, [gallery.items, applyServerStatuses]);

  /* ─────────────────────────── Selection ──────────────────────────────── */

  const toggleSelect = useCallback(
    (fileId: string, event: React.MouseEvent | React.KeyboardEvent) => {
      const index = gallery.items.findIndex((item) => item.id === fileId);

      setSelected((current) => {
        const next = new Set(current);

        // Shift extends from the last anchor, the way every file manager and
        // photo app behaves.
        if (
          "shiftKey" in event &&
          event.shiftKey &&
          lastClickedIndex.current !== null
        ) {
          const [from, to] = [lastClickedIndex.current, index].sort(
            (a, b) => a - b,
          ) as [number, number];
          for (let i = from; i <= to; i++) {
            const item = gallery.items[i];
            if (item) next.add(item.id);
          }
          return next;
        }

        if (next.has(fileId)) next.delete(fileId);
        else next.add(fileId);
        return next;
      });

      lastClickedIndex.current = index;
    },
    [gallery.items],
  );

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    lastClickedIndex.current = null;
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(gallery.items.map((item) => item.id)));
  }, [gallery.items]);

  /* ──────────────────────────── Actions ───────────────────────────────── */

  const favorite = useMutation({
    mutationFn: (input: { fileId: string; favorited: boolean }) =>
      trpcClient.media.favorite.mutate({ groupId, ...input }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["gallery", groupId] }),
  });

  const remove = useMutation({
    mutationFn: (fileIds: string[]) =>
      trpcClient.media.delete.mutate({ groupId, fileIds }),
    onSuccess: (result) => {
      clearSelection();
      void queryClient.invalidateQueries({ queryKey: ["gallery", groupId] });
      void group.refetch();
      toast(
        result.skipped > 0
          ? `Moved ${result.deleted} to trash. ${result.skipped} weren't yours to delete.`
          : `Moved ${result.deleted} ${result.deleted === 1 ? "item" : "items"} to trash.`,
        {
          variant: "success",
          /*
           * The way to the trash, at the one moment somebody has just used it.
           * A message naming a place nobody can find is how "move to trash"
           * came to read as a polite word for delete.
           */
          action: {
            label: "Show trash",
            onClick: () => {
              void navigate({
                to: "/g/$groupId",
                params: { groupId },
                search: { view: "trash" as const },
              });
            },
          },
        },
      );
    },
    onError: (error) => toast(error.message, { variant: "danger" }),
  });

  const move = useMutation({
    mutationFn: (input: { fileIds: string[]; folderId: string | null }) =>
      trpcClient.groups.moveFiles.mutate({ groupId, ...input }),
    onSuccess: (result, input) => {
      clearSelection();
      void queryClient.invalidateQueries({ queryKey: ["gallery", groupId] });
      // Album cards show counts and covers, so they go stale on every move.
      void folders.refetch();

      const album = albums.find((candidate) => candidate.id === input.folderId);
      const noun = result.moved === 1 ? "item" : "items";
      toast(
        album
          ? `Added ${result.moved} ${noun} to ${album.name}.`
          : `Removed ${result.moved} ${noun} from their album.`,
        { variant: "success" },
      );
    },
    onError: (error) => toast(error.message, { variant: "danger" }),
  });

  /* ──────────────────────────── Lightbox ──────────────────────────────── */

  /*
   * Indexed into what is on screen, not into the whole library.
   *
   * Otherwise, with somebody chosen, opening one of their photographs and
   * pressing next walks straight into everybody else's — the filter would
   * apply to the grid and quietly stop applying the moment you looked at
   * something.
   */
  const lightboxIndex = useMemo(
    () =>
      search.photo
        ? visibleItems.findIndex((item) => item.id === search.photo)
        : -1,
    [search.photo, visibleItems],
  );

  /**
   * Adding photographs, with the album asked for when the screen has none.
   *
   * Inside an album the answer is obvious and nobody is interrupted. From All
   * albums — which is where most people are — the folder came from the URL,
   * there was none, and every upload went in unfiled without a word.
   */
  const addPhotos = () => {
    if (search.folder) {
      uploads.pick(search.folder);
      return;
    }
    setPickingAlbum(true);
  };

  const openPhoto = useCallback(
    (fileId: string) => {
      void navigate({
        to: "/g/$groupId",
        params: { groupId },
        search: { ...search, photo: fileId },
      });
    },
    [navigate, groupId, search],
  );

  const closePhoto = useCallback(() => {
    void navigate({
      to: "/g/$groupId",
      params: { groupId },
      search: { ...search, photo: undefined },
    });
  }, [navigate, groupId, search]);

  /* ───────────────────────────── Render ───────────────────────────────── */

  const activeFolder = folders.data?.find(
    (folder) => folder.id === search.folder,
  );
  const isFiltered = Boolean(
    search.folder ||
    search.kind ||
    search.by ||
    search.year ||
    search.favorites,
  );

  if (search.view === "trash") {
    return (
      <TrashView
        groupId={groupId}
        canRestoreAny={capabilities.has("file:restore")}
      />
    );
  }

  if (search.view === "albums") {
    return (
      <AlbumsView
        groupId={groupId}
        folders={(folders.data ?? []) as unknown as Album[]}
        canCreate={capabilities.has("folder:create")}
        isLoading={folders.isLoading}
        onChanged={() => void folders.refetch()}
      />
    );
  }

  return (
    <UploadDropzone
      disabled={!capabilities.has("file:upload")}
      onFiles={uploads.enqueue}
      className="fc-gallery"
    >
      <header className="fc-gallery__header">
        <div className="fc-gallery__heading">
          {/*
            The group, once.

            The switcher and the heading both named it, so looking at a group
            meant reading its name twice, one line apart. The switcher is the
            one that survives, because it is also the way out of the group —
            and it is always there, so what the heading says underneath can be
            about where you are *within* the group instead.
          */}
          <GroupBar groupId={groupId} />

          <h1 data-scope={activeFolder ? "album" : "group"}>
            {activeFolder ? (
              <>
                {activeFolder.emoji && (
                  <span aria-hidden="true">
                    <EmojiIcon emoji={activeFolder.emoji} />{" "}
                  </span>
                )}
                {activeFolder.name}
              </>
            ) : search.favorites ? (
              t("Favourites")
            ) : (
              <>
                {group.data?.group.emoji && (
                  <span className="fc-gallery__glyph" aria-hidden="true">
                    <EmojiIcon emoji={group.data.group.emoji} />
                  </span>
                )}
                {group.data?.group.name ?? t("Photos")}
              </>
            )}
          </h1>
          {activeFolder && (
            <p className="fc-gallery__subtitle">
              {activeFolder.photoCount} photos · {activeFolder.videoCount}{" "}
              videos
              {activeFolder.locationName
                ? ` · ${activeFolder.locationName}`
                : ""}
            </p>
          )}
        </div>

        <div className="fc-gallery__actions">
          {/*
            One group, not four squares.

            These were four separately-bordered tiles sitting the same
            distance from each other as from the primary button, so the row
            read as five equal things of which one happened to be purple.
            Sharing a border makes them a set of related actions, and puts a
            real gap between that set and the one thing on this screen most
            people came to do.
          */}
          <div className="fc-gallery__tools" role="group">
            {/*
            Editing the album you are looking at, from the album you are looking
            at. "New album" while standing inside one answers a question nobody
            asked; the useful button here is the one that fixes its name.
          */}
            {activeFolder && capabilities.has("folder:create") ? (
              <Button
                variant="secondary"
                leadingIcon={<AlbumEditIcon />}
                title={t("Edit album")}
                onClick={() => setAlbumDialog(activeFolder)}
              >
                {t("Edit album")}
              </Button>
            ) : (
              capabilities.has("folder:create") && (
                <Button
                  variant="secondary"
                  leadingIcon={<AlbumPlusIcon />}
                  title={t("New album")}
                  onClick={() => setAlbumDialog("new")}
                >
                  {t("New album")}
                </Button>
              )
            )}
            {/*
            A toggle, not a one-way door. The strip carries its own "Hide", but
            on a wide screen that sits at the far right edge, a long way from
            the button that opened it — so the obvious way to close something is
            to press again whatever opened it.
          */}
            {gallery.items.some((item) => item.kind === "photo") && (
              <Button
                variant="secondary"
                leadingIcon={<PeopleIcon />}
                title={t("People")}
                aria-pressed={findingPeople}
                onClick={() => {
                  setFindingPeople((showing) => !showing);
                  if (findingPeople) setPerson(null);
                }}
              >
                {t("People")}
              </Button>
            )}

            {gallery.items.some((item) => item.kind === "photo") && (
              <Button
                variant="secondary"
                leadingIcon={<DuplicatesIcon />}
                title={t("Find duplicates")}
                onClick={() => setFindingDupes(true)}
              >
                {t("Find duplicates")}
              </Button>
            )}

            {/* Nothing to play before anything has been added. */}
            {gallery.items.some((item) => item.kind === "photo") && (
              <Button
                variant="secondary"
                leadingIcon={<PlayIcon />}
                title={t("Play")}
                onClick={() => setPlaying(true)}
              >
                {t("Play")}
              </Button>
            )}
          </div>

          {capabilities.has("file:upload") && (
            <Button
              variant="primary"
              leadingIcon={<AddPhotosIcon />}
              onClick={addPhotos}
            >
              {t("Add photos")}
            </Button>
          )}
        </div>
      </header>

      <FilterBar
        groupId={groupId}
        folders={folders.data ?? []}
        members={memberOptions}
        years={years}
        search={search}
        totalLoaded={gallery.items.length}
      />

      {/*
        Always rendered, empty or not.

        The row this sits in is part of a fixed grid template, and grid assigns
        rows by position. When this element came and went, showing People moved
        the photo grid out of the `1fr` row and put the strip in it instead —
        so the strip stretched to the full height of the page, its own rows
        spread apart to fill it, and the photographs were pushed below the fold.
        A wrapper that is always present keeps every row where it belongs; it
        collapses to nothing when there is no strip inside it.
      */}
      <div className="fc-gallery__strip">
        {findingPeople && (
          <Suspense fallback={null}>
            <PeopleStrip
              groupId={groupId}
              photos={gallery.items.map((item) => ({
                id: item.id,
                kind: item.kind,
              }))}
              loadEverything={async () => {
                while (gallery.hasNextPage) {
                  await gallery.fetchNextPage();
                }
              }}
              /*
               * Inside an album, the strip shows the people in that album.
               *
               * Any filter narrows it, not just a chosen album: looking at
               * 2024, or at videos, the question is still "who is in these
               * photographs". Clustering itself always runs over the whole
               * group, so nobody gains or loses an identity by walking into a
               * different album — only the list of who is shown changes.
               */
              narrowed={
                Boolean(search.folder) ||
                Boolean(search.year) ||
                Boolean(search.by) ||
                Boolean(search.favorites) ||
                search.kind === "video"
              }
              canName={capabilities.has("file:organize")}
              /* Names to offer, not accounts to link: see the prop's own note. */
              members={memberNames}
              selected={person}
              onSelect={setPerson}
              onClose={() => {
                setFindingPeople(false);
                setPerson(null);
              }}
            />
          </Suspense>
        )}
      </div>

      {gallery.isLoading ? (
        <GallerySkeleton />
      ) : gallery.items.length === 0 ? (
        <EmptyState
          icon={<PhotoIcon />}
          title={
            isFiltered ? t("Nothing matches those filters") : t("No photos yet")
          }
          description={
            isFiltered
              ? t(
                  "Try widening the date range, or clear the filters to see everything.",
                )
              : t(
                  "Start building your trip memories. Drag photos anywhere on this page, or use the button above.",
                )
          }
          action={
            isFiltered ? (
              <Button
                variant="secondary"
                onClick={() =>
                  navigate({
                    to: "/g/$groupId",
                    params: { groupId },
                    search: {},
                  })
                }
              >
                {t("Clear filters")}
              </Button>
            ) : capabilities.has("file:upload") ? (
              <Button variant="primary" onClick={addPhotos}>
                {t("Add photos")}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <PhotoGrid
          sections={sections}
          thumbRefs={gallery.thumbRefs}
          thumbsUnavailable={gallery.thumbsUnavailable}
          sharedFileIds={sharedFileIds}
          /*
           * A rebuilt thumbnail is a new object, and the signing answers cached
           * for this page still say there is none. Dropping them is what makes
           * the tile fill in rather than keep its "No preview".
           */
          onRebuilt={() => {
            void queryClient.invalidateQueries({
              queryKey: ["thumbUrls", groupId],
            });
          }}
          groupId={groupId}
          selected={selected}
          selectionMode={selected.size > 0}
          /*
           * Draggable only while the People strip is open, and only for
           * somebody allowed to name people — dragging a photograph onto
           * somebody writes a name, so it needs the same permission naming
           * does. Everywhere else a draggable tile just turns slow clicks into
           * drags that go nowhere.
           */
          assignable={findingPeople && capabilities.has("file:organize")}
          onOpen={openPhoto}
          onToggleSelect={toggleSelect}
          onLoadMore={() => void gallery.fetchNextPage()}
          hasMore={Boolean(gallery.hasNextPage)}
          isLoadingMore={gallery.isFetchingNextPage}
          density={search.density ?? "comfortable"}
        />
      )}

      {selected.size > 0 && (
        <SelectionBar
          count={selected.size}
          totalBytes={gallery.items
            .filter((item) => selected.has(item.id))
            .reduce((total, item) => total + item.sizeBytes, 0)}
          canDelete={
            capabilities.has("file:delete_own") ||
            capabilities.has("file:delete_any")
          }
          canDownload={capabilities.has("file:download")}
          canShare={capabilities.has("share:create")}
          canOrganize={capabilities.has("file:organize")}
          albums={albums as unknown as Album[]}
          onSelectAll={selectAll}
          onClear={clearSelection}
          onShare={() => setSharing([...selected])}
          onDownload={() =>
            void exporter.start(
              /* Names travel with the ids: the archive needs them, and the grid
                 already has them. */
              gallery.items
                .filter((item) => selected.has(item.id))
                .map((item) => ({
                  id: item.id,
                  name: item.name,
                  sizeBytes: item.sizeBytes,
                })),
            )
          }
          onDelete={() => remove.mutate([...selected])}
          onMove={(folderId) =>
            move.mutate({ fileIds: [...selected], folderId })
          }
          isDeleting={remove.isPending}
          isMoving={move.isPending}
        />
      )}

      <ExportDock
        {...exporter}
        onSave={exporter.save}
        onDismiss={exporter.dismiss}
      />

      <UploadPanel
        tasks={uploads.tasks}
        active={uploads.active}
        progress={uploads.progress}
        completedCount={uploads.completedCount}
        failedCount={uploads.failedCount}
        totalBytes={uploads.totalBytes}
        uploadedBytes={uploads.uploadedBytes}
        settled={uploads.settled}
        onRetry={uploads.retry}
        onCancel={uploads.cancel}
        onDismiss={uploads.clearFinished}
      />

      {albumDialog && (
        <AlbumDialog
          groupId={groupId}
          folder={albumDialog === "new" ? null : albumDialog}
          onClose={() => setAlbumDialog(null)}
          onSaved={(folderId: string) => {
            const wasNew = albumDialog === "new";
            setAlbumDialog(null);
            void folders.refetch();
            // Drop straight into the album that was just made — creating one
            // and staying put makes it feel like nothing happened. Editing the
            // one already open should not move anybody anywhere.
            if (wasNew) {
              void navigate({
                to: "/g/$groupId",
                params: { groupId },
                search: { folder: folderId },
              });
            }

            /* They were on their way to add photographs when they stopped to
               make somewhere to put them. Carry on. */
            if (uploadAfterAlbum.current) {
              uploadAfterAlbum.current = false;
              uploads.pick(folderId);
            }
          }}
        />
      )}

      {pickingAlbum && (
        <AlbumPicker
          albums={folders.data ?? []}
          canCreate={capabilities.has("folder:create")}
          onClose={() => setPickingAlbum(false)}
          onChoose={(folderId) => {
            setPickingAlbum(false);
            uploads.pick(folderId);
          }}
          onCreateAlbum={() => {
            setPickingAlbum(false);
            setAlbumDialog("new");
            uploadAfterAlbum.current = true;
          }}
        />
      )}

      {sharing && (
        <ShareDialog
          groupId={groupId}
          fileIds={sharing}
          onClose={() => {
            setSharing(null);
            clearSelection();
          }}
        />
      )}

      {lightboxIndex >= 0 && (
        <Lightbox
          groupId={groupId}
          items={visibleItems}
          index={lightboxIndex}
          canDownloadOriginal={capabilities.has("file:download_original")}
          canComment={capabilities.has("comment:create")}
          thumbRefs={gallery.thumbRefs}
          members={memberDirectory}
          onClose={closePhoto}
          onNavigate={(index) => {
            const item = visibleItems[index];
            if (item) openPhoto(item.id);
          }}
          onToggleFavorite={(fileId, favorited) =>
            favorite.mutate({ fileId, favorited })
          }
          onEdit={
            capabilities.has("file:upload")
              ? (fileId) => {
                  const target = gallery.items.find(
                    (candidate) => candidate.id === fileId,
                  );
                  if (target) {
                    setEditing({
                      id: target.id,
                      name: target.name,
                      mime: target.mime,
                    });
                  }
                }
              : undefined
          }
        />
      )}

      {findingDupes && (
        <DuplicatesPanel
          groupId={groupId}
          items={slideshowItems}
          thumbRefs={gallery.thumbRefs}
          /*
           * A scan of half a library is worse than no scan: it reports sets
           * that are incomplete and misses the ones split across a page
           * boundary. Every remaining page is pulled before anything is
           * compared.
           */
          loadEverything={async () => {
            while (gallery.hasNextPage) {
              await gallery.fetchNextPage();
            }
          }}
          canDelete={
            capabilities.has("file:delete_own") ||
            capabilities.has("file:delete_any")
          }
          onDelete={(fileIds) => {
            remove.mutate(fileIds);
            setFindingDupes(false);
          }}
          onClose={() => setFindingDupes(false)}
        />
      )}

      {playing && (
        <Slideshow
          groupId={groupId}
          title={activeFolder?.name}
          items={slideshowItems}
          onClose={() => setPlaying(false)}
        />
      )}

      {editing && (
        <PhotoEditor
          groupId={groupId}
          file={editing}
          onClose={() => setEditing(null)}
          onSave={(edited) => {
            /*
             * The edited copy joins the ordinary upload queue: same sealing,
             * same derivatives, same quota reservation. Nothing about it is a
             * special case once it is a File, which is why the editor hands
             * back one rather than uploading anything itself.
             */
            uploads.enqueue([edited]);
            setEditing(null);
            toast("Saved as a copy. The original is untouched.");
          }}
        />
      )}
    </UploadDropzone>
  );
}

function GallerySkeleton() {
  return (
    <div className="fc-gallery__skeleton" aria-hidden="true">
      {Array.from({ length: 18 }, (_, index) => (
        <Skeleton key={index} height="100%" radius="var(--fc-tile-radius)" />
      ))}
    </div>
  );
}

function PhotoIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="10" r="1.5" />
      <path
        d="M21 16l-5-5-4.5 4.5L9 13l-6 6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
